import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import RotateDevice from "../../components/RotateDevice/RotateDevice";
import Lf23Loader from "../../components/Lf23Loader";
import { FiltroMulti, valoriDi, opzioniDa, BadgeValidita, FiltroValidita, CampiValidita, attivaIl, AvvisoSenzaZona, clicFuori } from "./comuni";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// Anagrafica Percorsi = community Interacta 121. CRUD completa.
// Un percorso anagrafico è: nome + itinerario (8 char) + elenco ordinato di tratti
// (presi dall'anagrafica 120). NON ha data, assegnatario né stato workflow (quelli
// vivono sulla 122 "Percorsi Misto", che qui è solo monitoraggio).
const EMPTY_FORM = { id: null, nome: "", itinerario: "", validoDal: "", validoAl: "", tipoPercorso: "ordinario" };

/* "Tipo percorso" (campo 1541 della 121, task 541): i percorsi di Bonifica restano separati
   nei giri ma nel report si raggruppano in una riga sola. Campo vuoto = ordinario. */
const TIPI_PERCORSO = [
    { value: "ordinario", label: "Ordinario" },
    { value: "bonifica", label: "Bonifica" },
];
const PillBonifica = ({ p }) => (p.tipoPercorso === "bonifica" ? <span className="gp-bonifica-pill">Bonifica</span> : null);

// Giorno (LUN..DOM) e Tipo (M/P/N) sono ricavati dal nome/itinerario lato backend.
const GIORNI = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
const TIPI = ["M", "P", "N"];
// Data odierna in ISO (YYYY-MM-DD), calcolata in locale (non UTC, per evitare scarti di un
// giorno a cavallo della mezzanotte). È la data di default del post operativo se non scelta.
const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const TabAnagraficaPercorsi = ({ zona }) => {
    const [dati, setDati] = useState([]);
    const [stats, setStats] = useState({ totali: 0 });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [showFilters, setShowFilters] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [query, setQuery] = useState({ search: "", giorno: "", tipo: "", tipoPercorso: "", minTratti: "", maxTratti: "", sortKey: "", sortDir: "asc" });
    // Validità (switch percorsi): di default le correnti, cioè attive + in arrivo.
    const [fValidita, setFValidita] = useState("correnti");
    // pannello filtri (applicati su "Applica")
    const [fGiorno, setFGiorno] = useState([]);
    const [fTipo, setFTipo] = useState([]);
    const [fTipoPercorso, setFTipoPercorso] = useState([]);
    const [fMin, setFMin] = useState("");
    const [fMax, setFMax] = useState("");

    // vista dati: tabella (default da 1025px in su) oppure card (default da 1024px in giù)
    const [viewMode, setViewMode] = useState(() => (window.matchMedia("(max-width: 1024px)").matches ? "cards" : "table"));
    // tabella a tutto schermo (fixed sull'intero viewport, con invito a ruotare su mobile)
    const [isFullscreen, setIsFullscreen] = useState(false);

    // composer crea/modifica
    const [composerOpen, setComposerOpen] = useState(false);
    const [cForm, setCForm] = useState(EMPTY_FORM);
    const [selectedTratti, setSelectedTratti] = useState([]);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState("");

    // indice tratti (per il picker e per risolvere i trattoPostIds in modifica)
    const [trattiList, setTrattiList] = useState(null); // null = non ancora caricato
    const [trattiLoading, setTrattiLoading] = useState(false);
    const [pickerSearch, setPickerSearch] = useState("");
    const [removeViaTarget, setRemoveViaTarget] = useState(null); // via in attesa di conferma rimozione

    // elimina
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState("");
    const [deleting, setDeleting] = useState(false);

    // genera percorso operativo (122 col deep-link) da un'anagrafica 121
    const [genTarget, setGenTarget] = useState(null); // anagrafica scelta
    const [genData, setGenData] = useState("");
    const [genOperatori, setGenOperatori] = useState([]); // array di { value, label }
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState("");
    const [genResult, setGenResult] = useState(null); // { postId, customId, url }
    const [genTratti, setGenTratti] = useState([]);          // vie del post 122 (modificabili SOLO per questo post)
    // Priorità AUTOMATICHE di recupero (issue #460): mappa codice tratta -> "gialla"/"rossa",
    // calcolata dal backend sullo storico delle mancate esecuzioni. Sola lettura: si spegne
    // da sola quando la tratta viene fatta. Convive con la stella manuale (t.prioritaria).
    const [recuperoMap, setRecuperoMap] = useState({});
    const [genPickerSearch, setGenPickerSearch] = useState("");
    // riordino: su desktop (>=1025px) trascinamento con maniglia (dragIndex = riga trascinata),
    // su mobile/tablet (<=1024px) le frecce su/giù. Entrambi mutano SOLO genTratti (post 122).
    const [dragIndex, setDragIndex] = useState(null);
    // riga appena spostata con le frecce (mobile): evidenziata ~1s per non perderla di vista
    const [movedId, setMovedId] = useState(null);
    const movedTimerRef = useRef(null);
    // Numerino d'ordine editabile: quale riga è in editing (id) e cosa c'è scritto dentro.
    // Il testo è separato dalla posizione reale così si possono digitare 2 cifre senza che
    // la riga salti a ogni tasto: lo spostamento avviene solo su Invio / uscita dal campo.
    const [idxEditId, setIdxEditId] = useState(null);
    const [idxEditVal, setIdxEditVal] = useState("");
    const [utentiList, setUtentiList] = useState(null); // null = non caricato
    const [utentiLoading, setUtentiLoading] = useState(false);

    // ---- fetch ----
    const fetchData = useCallback(async (opts = {}) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/anagrafica-percorsi`, {
                headers: authHeaders(),
                params: {
                    zona,
                    page,
                    limit,
                    search: query.search || undefined,
                    giorno: query.giorno || undefined,
                    tipo: query.tipo || undefined,
                    tipoPercorso: query.tipoPercorso || undefined,
                    minTratti: query.minTratti || undefined,
                    maxTratti: query.maxTratti || undefined,
                    validita: fValidita,
                    sortKey: query.sortKey || undefined,
                    sortDir: query.sortDir || undefined,
                    refresh: opts.refresh ? 1 : undefined,
                },
            });
            setDati(res.data?.data ?? []);
            const pag = res.data?.pagination ?? {};
            setTotalPages(pag.totalPages ?? 1);
            setTotalItems(pag.totalItems ?? 0);
            setStats(res.data?.stats ?? { totali: 0 });
        } catch (err) {
            console.error("Errore nel fetch anagrafica percorsi:", err);
            setDati([]);
        } finally {
            setLoading(false);
        }
    }, [zona, page, limit, query, fValidita]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // debounce ricerca libera
    useEffect(() => {
        const t = setTimeout(() => {
            setQuery((q) => (q.search === searchInput ? q : { ...q, search: searchInput }));
            setPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    // ESC esce dalla tabella a tutto schermo
    useEffect(() => {
        if (!isFullscreen) return;
        const onEsc = (e) => { if (e.key === "Escape") setIsFullscreen(false); };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [isFullscreen]);

    // Cambio vista automatico al superamento della soglia 1024px, SENZA ricaricare:
    // <=1024 -> card, >=1025 -> tabella (riapplica il default della fascia).
    // "change" scatta solo attraversando la soglia, non a ogni resize.
    useEffect(() => {
        const mql = window.matchMedia("(max-width: 1024px)");
        const onChange = (e) => setViewMode(e.matches ? "cards" : "table");
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
    }, []);

    const applyFilters = () => {
        setQuery((q) => ({ ...q, search: searchInput, giorno: valoriDi(fGiorno).join(","), tipo: valoriDi(fTipo).join(","), tipoPercorso: valoriDi(fTipoPercorso).join(","), minTratti: fMin, maxTratti: fMax }));
        setPage(1);
    };
    const resetFilters = () => {
        setFGiorno([]); setFTipo([]); setFTipoPercorso([]); setFMin(""); setFMax(""); setSearchInput("");
        setQuery({ search: "", giorno: "", tipo: "", tipoPercorso: "", minTratti: "", maxTratti: "", sortKey: "", sortDir: "asc" });
        setPage(1);
    };

    const toggleSort = (key) => {
        setQuery((q) => ({ ...q, sortKey: key, sortDir: q.sortKey === key && q.sortDir === "asc" ? "desc" : "asc" }));
        setPage(1);
    };
    const sortArrow = (key) => (query.sortKey === key ? (query.sortDir === "asc" ? " ▲" : " ▼") : "");

    // ---- indice tratti (lazy) ----
    const ensureTrattiList = useCallback(async () => {
        if (trattiList) return trattiList;
        setTrattiLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/tratti`, {
                headers: authHeaders(),
                // Nel composer si scelgono solo tratte della zona del percorso.
                params: { zona, page: 1, limit: 100000 },
            });
            const list = res.data?.data ?? [];
            setTrattiList(list);
            return list;
        } catch (err) {
            console.error("Errore nel caricamento dei tratti:", err);
            setTrattiList([]);
            return [];
        } finally {
            setTrattiLoading(false);
        }
    }, [zona, trattiList]);

    // ---- operatori (assegnatari) per la generazione, lazy ----
    const ensureUtenti = useCallback(async () => {
        if (utentiList) return utentiList;
        setUtentiLoading(true);
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/utenti`, { headers: authHeaders() });
            const list = res.data?.data ?? [];
            setUtentiList(list);
            return list;
        } catch (err) {
            console.error("Errore nel caricamento degli operatori:", err);
            setUtentiList([]);
            return [];
        } finally {
            setUtentiLoading(false);
        }
    }, [utentiList]);

    const utentiOptions = useMemo(() => (utentiList || []).map((u) => ({ value: u.id, label: u.nome })), [utentiList]);

    // ---- composer ----
    const openNew = async () => {
        setCForm(EMPTY_FORM);
        setSelectedTratti([]);
        setPickerSearch("");
        setFormError("");
        setComposerOpen(true);
        ensureTrattiList();
    };

    const openEdit = async (p) => {
        setComposerOpen(true);
        setPickerSearch("");
        setFormError("");
        setCForm({ id: p.id, nome: p.nome || "", itinerario: p.itinerario || "", validoDal: p.validoDal || "", validoAl: p.validoAl || "", tipoPercorso: p.tipoPercorso || "ordinario" });
        const list = await ensureTrattiList();
        const byId = new Map(list.map((t) => [t.id, t]));
        const sel = (p.trattoPostIds || []).map((id) => byId.get(id) || { id, codice: "?", strada: "(tratto non trovato)", tratto: "" });
        setSelectedTratti(sel);
    };

    const pickerResults = useMemo(() => {
        if (!trattiList) return [];
        const s = pickerSearch.trim().toLowerCase();
        if (!s) return [];
        const selectedIds = new Set(selectedTratti.map((t) => t.id));
        return trattiList
            .filter((t) => !selectedIds.has(t.id))
            // le archiviate non entrano in un percorso: non verranno più spazzate
            .filter((t) => t.validita !== "archiviata")
            .filter((t) =>
                (t.codice || "").toLowerCase().includes(s) ||
                (t.strada || "").toLowerCase().includes(s) ||
                (t.tratto || "").toLowerCase().includes(s))
            .slice(0, 25);
    }, [trattiList, pickerSearch, selectedTratti]);

    // Non svuoto la ricerca dopo l'aggiunta: così se ne aggiungono diversi di fila.
    const addTratto = (t) => setSelectedTratti((arr) => [...arr, t]);
    const removeTratto = (id) => setSelectedTratti((arr) => arr.filter((t) => t.id !== id));

    // Riordino dei tratti del percorso ANAGRAFICO (community 121). A differenza del
    // generatore, qui l'ordine è DEFINITIVO: savePercorso invia trattoPostIds in questo
    // ordine a Interacta, quindi il riordino è permanente (creazione e modifica).
    const moveTratto = (from, to) => {
        setSelectedTratti((arr) => {
            if (from == null || to == null || from === to || from < 0 || to < 0) return arr;
            const next = [...arr];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    // Una riga "ha la stella" se è spuntata a mano OPPURE se la tratta è da recuperare
    // (priorità automatica). Le due cose convivono e valgono uguale per l'ordinamento.
    const haStella = (t, rec = recuperoMap) => !!t?.prioritaria || !!rec[t?.codice];

    // Ordinamento per stelle: le tratte stellate vanno in testa, le altre seguono.
    // Partizione STABILE (due filter, non un sort): dentro ciascun gruppo l'ordine del
    // tragitto è preservato, quindi se le stelle sono già contigue in testa non si muove
    // nulla. Non ordina per livello: una rossa non scavalca una gialla che la precede.
    const ordinaPerStelle = (arr, rec = recuperoMap) => [
        ...arr.filter((t) => haStella(t, rec)),
        ...arr.filter((t) => !haStella(t, rec)),
    ];

    // Priorità PER-POST: scelta locale sui tratti di QUESTO post. Non scrive subito su
    // Interacta: i codici prioritari vengono salvati sul post 122 solo al "Genera".
    // La stella è anche una scorciatoia di ordinamento ("semiautomatico"): accendendola
    // la tratta sale in testa, accodandosi alle prioritarie già presenti lì.
    // Spegnendola la riga NON si muove: da lì la si sposta a mano (drag/frecce/numerino).
    const toggleGenPriorita = (id) => {
        setGenTratti((arr) => {
            const from = arr.findIndex((t) => t.id === id);
            if (from < 0) return arr;
            const accesa = !arr[from].prioritaria;
            const next = arr.map((t) => (t.id === id ? { ...t, prioritaria: accesa } : t));
            if (!accesa) return next; // stella tolta: cambia solo il colore, l'ordine resta
            // Se la riga ha GIÀ una stella automatica è di per sé dentro al blocco in testa:
            // aggiungerci la spunta manuale non deve spostarla (la manderebbe in fondo al
            // blocco). Questo è anche ciò che tiene valido l'invariante "to <= from" qui sotto.
            if (haStella(arr[from])) return next;
            // Quante righe stellate ci sono in un blocco contiguo dall'inizio? La nuova si
            // accoda subito dopo. Il conteggio è su "arr" (stato PRIMA dell'accensione) e la
            // riga corrente non è stellata, quindi non è nel blocco e "to" non supera "from".
            let to = 0;
            while (to < arr.length && haStella(arr[to])) to++;
            return moveInArray(next, from, to);
        });
        flashMoved(id);      // evidenzia ~1s la riga arrivata in cima
        scrollToTratto(id);  // e la porta in vista (il box selezionati ha scroll interno)
    };

    // ---- picker tratti del modale "Genera" (gemello del picker del composer) ----
    const genPickerResults = useMemo(() => {
        if (!trattiList) return [];
        const s = genPickerSearch.trim().toLowerCase();
        if (!s) return [];
        const selectedIds = new Set(genTratti.map((t) => t.id));
        return trattiList
            .filter((t) => !selectedIds.has(t.id))
            // solo le tratte valide per il giorno del giro (il backend lo verifica comunque)
            .filter((t) => !genData || attivaIl(t, genData))
            .filter((t) =>
                (t.codice || "").toLowerCase().includes(s) ||
                (t.strada || "").toLowerCase().includes(s) ||
                (t.tratto || "").toLowerCase().includes(s))
            .slice(0, 25);
    }, [trattiList, genPickerSearch, genTratti, genData]);

    // Rimozione DIRETTA (no conferma): tocca solo il post che stiamo creando, non l'anagrafica.
    // Tratta aggiunta dal picker: se ha già la stella (automatica) entra in coda al blocco
    // stellato in testa, non in fondo alla lista, così le stelle restano contigue.
    const addGenTratto = (t) =>
        setGenTratti((arr) => {
            if (!haStella(t)) return [...arr, t];
            let to = 0;
            while (to < arr.length && haStella(arr[to])) to++;
            return [...arr.slice(0, to), t, ...arr.slice(to)];
        });
    const removeGenTratto = (id) => setGenTratti((arr) => arr.filter((t) => t.id !== id));

    // Sposta l'elemento da "from" a "to" restituendo un NUOVO array (non muta l'originale:
    // React si accorge del cambiamento solo se cambia il riferimento). Lo splice fa
    // esattamente lo "scalamento" voluto: tolgo l'elemento dalla vecchia posizione e lo
    // reinserisco nella nuova, tutti quelli in mezzo scorrono di uno da soli.
    const moveInArray = (arr, from, to) => {
        if (from == null || to == null || from === to || from < 0 || to < 0) return arr;
        if (from >= arr.length || to >= arr.length) return arr;
        const next = [...arr];
        const [moved] = next.splice(from, 1); // tolgo l'elemento dalla vecchia posizione
        next.splice(to, 0, moved);            // lo reinserisco alla nuova
        return next;
    };

    // Riordino drag-and-drop: sposta il tratto da "from" a "to" dentro genTratti.
    // Muta SOLO il post temporaneo (122): l'anagrafica 121 resta invariata. I numeri
    // blu (.idx) sono {i+1}, quindi si riallineano da soli appena cambia l'ordine.
    const moveGenTratto = (from, to) => setGenTratti((arr) => moveInArray(arr, from, to));

    // Applica il numero digitato nel quadratino d'ordine: 1 = prima posizione. Fuori range
    // viene "pinzato" tra 1 e il numero di tratte; campo vuoto o non numerico = nessuno
    // spostamento (è anche il caso dell'Esc, che rimette in campo la posizione attuale).
    const applyIdxEdit = (from) => {
        const raw = idxEditVal;
        setIdxEditId(null);
        setIdxEditVal("");
        const n = parseInt(raw, 10);
        if (!Number.isFinite(n)) return;
        const to = Math.min(Math.max(n, 1), genTratti.length) - 1; // da 1-based a indice array
        if (to === from) return;
        const id = genTratti[from]?.id;
        moveGenTratto(from, to);
        if (id != null) { flashMoved(id); scrollToTratto(id); }
    };

    // Evidenzia per ~1s la riga appena spostata con le frecce, così non la si perde di vista.
    // Traccia per id (stabile) e riavvia il timer a ogni click: resta evidenziata mentre sposti.
    const flashMoved = (id) => {
        setMovedId(id);
        if (movedTimerRef.current) clearTimeout(movedTimerRef.current);
        movedTimerRef.current = setTimeout(() => setMovedId(null), 1000);
    };
    useEffect(() => () => { if (movedTimerRef.current) clearTimeout(movedTimerRef.current); }, []);

    // Porta in vista la riga appena spostata (il box selezionati ha scroll interno, quindi
    // una tratta che vola in cima da posizione 40 finirebbe fuori dalla parte visibile).
    // requestAnimationFrame: aspetto che React abbia ridisegnato la lista nel nuovo ordine,
    // altrimenti cercherei la riga dove stava prima.
    const scrollToTratto = (id) => {
        requestAnimationFrame(() => {
            document
                .querySelector(`[data-tratto-id="${id}"]`)
                ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
    };

    const savePercorso = async () => {
        if (!cForm.nome.trim()) { setFormError("Il nome del percorso è obbligatorio."); return; }
        if (cForm.itinerario.trim().length !== 8) { setFormError("L'itinerario deve essere esattamente 8 caratteri (richiesto da Interacta)."); return; }
        if (selectedTratti.length === 0) { setFormError("Aggiungi almeno un tratto."); return; }
        setSaving(true);
        setFormError("");
        try {
            const body = {
                nome: cForm.nome,
                itinerario: cForm.itinerario,
                trattoPostIds: selectedTratti.map((t) => t.id),
                validoDal: cForm.validoDal || null,
                validoAl: cForm.validoAl || null,
                tipoPercorso: cForm.tipoPercorso || "ordinario",
            };
            if (cForm.id) {
                await axios.put(`${API_URL}/gestione-percorsi/anagrafica-percorsi/${cForm.id}`, body, { headers: authHeaders() });
            } else {
                // Un percorso nuovo appartiene alla zona del tab aperto; in modifica la zona non cambia.
                await axios.post(`${API_URL}/gestione-percorsi/anagrafica-percorsi`, { ...body, zona }, { headers: authHeaders() });
            }
            setComposerOpen(false);
            await fetchData();
        } catch (err) {
            console.error("Errore salvataggio percorso:", err);
            setFormError(err.response?.data?.error || "Errore nel salvataggio. Riprova.");
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setDeleteError("");
        try {
            await axios.delete(`${API_URL}/gestione-percorsi/anagrafica-percorsi/${deleteTarget.id}`, { headers: authHeaders() });
            setDeleteTarget(null);
            await fetchData();
        } catch (err) {
            console.error("Errore eliminazione percorso:", err);
            setDeleteError(err.response?.data?.error || "Errore nell'eliminazione. Riprova.");
        } finally {
            setDeleting(false);
        }
    };

    // ---- genera percorso operativo (122 col deep-link) ----
    const openGenera = async (p) => {
        setGenTarget(p);
        setGenData(todayISO());
        setGenOperatori([]);
        setGenPickerSearch("");
        setGenError("");
        setGenResult(null);
        ensureUtenti();
        // Pre-carico le vie dell'anagrafica (punto di partenza): l'utente le modifica solo per questo post.
        const list = await ensureTrattiList();
        const byId = new Map(list.map((t) => [t.id, t]));
        const base = (p.trattoPostIds || []).map((id) => byId.get(id) || { id, codice: "?", strada: "(tratto non trovato)", tratto: "" });
        // Stelle precompilate: se il calcolo non è disponibile si prosegue senza (nessuna stella).
        let rec = {};
        try {
            const res = await axios.get(`${API_URL}/gestione-percorsi/tratte-recupero`, { headers: authHeaders() });
            rec = res.data?.recupero || {};
        } catch (err) {
            console.error("Priorità di recupero non disponibili:", err);
        }
        setRecuperoMap(rec);
        // L'ordine di partenza è già quello "per stelle": si apre col lavoro da recuperare in testa.
        setGenTratti(ordinaPerStelle(base, rec));
    };

    const submitGenera = async () => {
        if (!genOperatori.length) { setGenError("Seleziona almeno un operatore."); return; }
        if (!genTratti.length) { setGenError("Seleziona almeno un tratto."); return; }
        setGenerating(true);
        setGenError("");
        try {
            const res = await axios.post(`${API_URL}/gestione-percorsi/percorsi/genera`, {
                anagraficaId: genTarget.id,
                data: genData || todayISO(),
                assegnatariIds: genOperatori.map((o) => o.value),
                trattoPostIds: genTratti.map((t) => t.id),
                // Solo le stelle spuntate a mano: alle automatiche pensa il backend, che le
                // unisce a queste prima di scrivere il campo 1119 letto dall'app mobile.
                trattePrioritarie: genTratti.filter((t) => t.prioritaria).map((t) => t.codice),
            }, { headers: authHeaders() });
            setGenResult({ postId: res.data?.postId, customId: res.data?.customId, url: res.data?.url });
        } catch (err) {
            console.error("Errore generazione percorso operativo:", err);
            const d = err.response?.data;
            if (err.response?.status === 409 && d?.existing) {
                setGenError(`Esiste già un percorso operativo per questo itinerario in questa data (${d.existing.customId || d.existing.postId}).`);
            } else {
                setGenError(d?.error || "Errore nella generazione. Riprova.");
            }
        } finally {
            setGenerating(false);
        }
    };

    // Paginazione condivisa tra vista tabella e vista card (stesso identico markup).
    const paginationBar = (
        <div className="pagination">
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg> Prec
            </button>
            <span className="page-info">Pag <strong>{page}</strong> di <strong>{totalPages}</strong></span>
            <span className="page-total">({totalItems} percorsi totali)</span>
            <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Succ <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
            </button>
            <div className="gp-perpage">
                <span>Per pagina:</span>
                {[10, 20, 50, 100].map((n) => (
                    <button key={n} className={`gp-perpage-btn ${limit === n ? "active" : ""}`} onClick={() => { setLimit(n); setPage(1); }}>{n}</button>
                ))}
            </div>
        </div>
    );

    return (
        <>
            {/* Loader a tutto schermo (parte subito al salvataggio/eliminazione) */}
            {(saving || deleting || generating) && (
                <div className="gp-save-overlay">
                    <div className="gp-save-card">
                        <Lf23Loader testo={deleting ? "Eliminazione in corso..." : generating ? "Generazione percorso operativo..." : "Salvataggio su Interacta..."} />
                    </div>
                </div>
            )}

            <div className="gp-toolbar-row">
            {/* Toggle filtri */}
            <button className={`toggle-filters-btn ${showFilters ? "active" : ""}`} onClick={() => setShowFilters((v) => !v)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                <svg className="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </button>

            {showFilters && (
                <div className="filters-panel">
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Giorno</label>
                            <FiltroMulti placeholder="Tutti" options={opzioniDa(GIORNI)}
                                value={fGiorno} onChange={setFGiorno} />
                        </div>
                        <div className="filter-group">
                            <label>Tipo</label>
                            <FiltroMulti placeholder="Tutti" options={opzioniDa(TIPI)}
                                value={fTipo} onChange={setFTipo} />
                        </div>
                        <div className="filter-group">
                            <label>Tipo percorso</label>
                            <FiltroMulti placeholder="Tutti" options={TIPI_PERCORSO}
                                value={fTipoPercorso} onChange={setFTipoPercorso} />
                        </div>
                        <div className="filter-group">
                            <label>Tratti (min)</label>
                            <input type="number" min="0" value={fMin} onChange={(e) => setFMin(e.target.value)} placeholder="es. 5" />
                        </div>
                        <div className="filter-group">
                            <label>Tratti (max)</label>
                            <input type="number" min="0" value={fMax} onChange={(e) => setFMax(e.target.value)} placeholder="es. 30" />
                        </div>
                    </div>
                    <div className="filter-actions">
                        <button className="btn btn-ghost" onClick={resetFilters}>Reset</button>
                        <button className="btn btn-primary" onClick={applyFilters}>Applica</button>
                    </div>
                </div>
            )}

            <div className="toolbar">
                <div className="search-box">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                    <input type="text" placeholder="Cerca percorso, customId, itinerario..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
                </div>
                <span className="gp-tb-break" aria-hidden="true" />
                <div className="spacer" />
                <button className="btn btn-ghost gp-new-btn" onClick={() => fetchData({ refresh: true })} title="Ricarica da Interacta (ignora la cache)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
                    Aggiorna
                </button>
                <span className="results-badge">{dati.length} di {totalItems}</span>
                <button className="btn btn-primary gp-new-btn" onClick={openNew}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Nuovo Percorso
                </button>
            </div>
            </div>

            {loading ? (
                <div className="gp-loading">
                    <Lf23Loader testo="Caricamento anagrafica percorsi in corso..." />
                </div>
            ) : (
            <>
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon blue"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 20l-5.5-2.5V4L9 6.5m0 13.5l6-3m-6 3V6.5m6 10.5l5.5 2.5V6L15 3.5m0 13.5V3.5" /></svg></div>
                    <div><p className="stat-label">Percorsi totali</p><p className="stat-value">{stats.totali}</p></div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon slate"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg></div>
                    <div><p className="stat-label">Risultati filtrati</p><p className="stat-value">{totalItems}</p></div>
                </div>
            </div>

            <AvvisoSenzaZona n={stats.senzaZona} cosa="percorsi" />

            <div className="gp-view-bar">
                <FiltroValidita value={fValidita} onChange={(v) => { setFValidita(v); setPage(1); }} conteggi={stats.validita} />
                <div className="view-toggle-compact">
                    <button className={`view-btn-compact ${viewMode === "table" ? "active" : ""}`} onClick={() => setViewMode("table")}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9h18M3 15h18M9 3v18" /></svg>
                        Tabella
                    </button>
                    <button className={`view-btn-compact ${viewMode === "cards" ? "active" : ""}`} onClick={() => setViewMode("cards")}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                        Card
                    </button>
                </div>
            </div>

            {viewMode === "table" ? (
            <div className={`table-container${isFullscreen ? " gp-fullscreen-table" : ""}`}>
                <RotateDevice active={isFullscreen} maxWidth={1024} />
                <div className="gp-table-topbar">
                    <button className="gp-fullscreen-btn" onClick={() => setIsFullscreen((v) => !v)} title={isFullscreen ? "Esci da tutto schermo" : "Tutto schermo"}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {isFullscreen ? (
                                <>
                                    <polyline points="4 14 10 14 10 20" />
                                    <polyline points="20 10 14 10 14 4" />
                                    <line x1="14" y1="10" x2="21" y2="3" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </>
                            ) : (
                                <>
                                    <polyline points="15 3 21 3 21 9" />
                                    <polyline points="9 21 3 21 3 15" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                </>
                            )}
                        </svg>
                    </button>
                </div>
                <div className="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => toggleSort("nome")} style={{ cursor: "pointer" }}>Percorso{sortArrow("nome")}</th>
                                <th onClick={() => toggleSort("itinerario")} style={{ cursor: "pointer" }}>Itinerario{sortArrow("itinerario")}</th>
                                <th onClick={() => toggleSort("nTratti")} style={{ cursor: "pointer" }}>Tratti{sortArrow("nTratti")}</th>
                                <th>Validità</th>
                                <th className="col-actions">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && dati.map((p) => (
                                <tr key={p.id}>
                                    <td><span className="strong">{p.nome || "—"}</span> <PillBonifica p={p} />{p.customId && <div className="codice" style={{ fontSize: 11 }}>{p.customId}</div>}</td>
                                    <td>{p.itinerario || "—"}</td>
                                    <td><span className="muted">{p.nTratti} vie</span></td>
                                    <td><BadgeValidita riga={p} /></td>
                                    <td className="col-actions">
                                        <button className="gp-genera-btn" title="Crea il post 122 su Interacta col link all'app operatori" onClick={() => openGenera(p)}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="12" y1="14" x2="12" y2="18" /><line x1="10" y1="16" x2="14" y2="16" /></svg>
                                            Genera Post<span className="gp-genera-extra"> Interacta</span>
                                        </button>
                                        <button className="icon-btn" title="Modifica" onClick={() => openEdit(p)}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                        </button>
                                        <button className="icon-btn danger" title="Elimina" onClick={() => { setDeleteError(""); setDeleteTarget(p); }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {dati.length === 0 && <div className="gp-empty">Nessun percorso trovato.</div>}
                </div>
                {paginationBar}
            </div>
            ) : (
            <>
                {dati.length === 0 ? (
                    <div className="gp-empty">Nessun percorso trovato.</div>
                ) : (
                    <div className="gp-cards-grid">
                        {dati.map((p) => (
                            <div className="gp-card" key={p.id}>
                                <div className="gp-card-head">
                                    <div className="gp-card-title">
                                        {p.nome || "—"} <PillBonifica p={p} />
                                        {p.customId && <span className="codice">{p.customId}</span>}
                                    </div>
                                </div>
                                <div className="gp-card-body">
                                    <div className="gp-card-row"><span className="gp-card-label">Itinerario</span><span className="gp-card-value">{p.itinerario || "—"}</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Tratti</span><span className="gp-card-value muted">{p.nTratti} vie</span></div>
                                    <div className="gp-card-row"><span className="gp-card-label">Validità</span><span className="gp-card-value"><BadgeValidita riga={p} /></span></div>
                                </div>
                                <div className="gp-card-actions">
                                    <button className="gp-genera-btn" title="Crea il post 122 su Interacta col link all'app operatori" onClick={() => openGenera(p)}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="12" y1="14" x2="12" y2="18" /><line x1="10" y1="16" x2="14" y2="16" /></svg>
                                        Genera Post<span className="gp-genera-extra"> Interacta</span>
                                    </button>
                                    <button className="icon-btn" title="Modifica" onClick={() => openEdit(p)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                    </button>
                                    <button className="icon-btn danger" title="Elimina" onClick={() => { setDeleteError(""); setDeleteTarget(p); }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {paginationBar}
            </>
            )}
            </>
            )}

            {/* ===== COMPOSER PERCORSO ===== */}
            {composerOpen && (
                <div className="modal-overlay" {...clicFuori(() => setComposerOpen(false))}>
                    <div className="modal wide gp-modal-full">
                        <div className="modal-header">
                            <div className="m-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 20l-5.5-2.5V4L9 6.5m0 13.5l6-3m-6 3V6.5m6 10.5l5.5 2.5V6L15 3.5m0 13.5V3.5" /></svg></div>
                            <div style={{ flex: 1 }}>
                                <h3>{cForm.id ? "Modifica percorso" : "Nuovo percorso"}</h3>
                                <div className="h-sub">{cForm.id ? "Aggiorna il post nella community 121 (Anagrafica Percorsi)" : "Verrà creato come post nella community 121 (Anagrafica Percorsi)"}</div>
                            </div>
                            <button className="modal-close" onClick={() => setComposerOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        {formError && <div className="modal-error">{formError}</div>}
                        <div className="modal-body">
                            <div className="form-grid">
                                <div className="form-group span2">
                                    <label>Nome percorso</label>
                                    <input type="text" value={cForm.nome} onChange={(e) => setCForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Es. 2-22 P LUN" />
                                </div>
                                <div className="form-group">
                                    <label>Itinerario (8 caratteri)</label>
                                    <input type="text" maxLength={8} value={cForm.itinerario} onChange={(e) => setCForm((f) => ({ ...f, itinerario: e.target.value }))} placeholder="Es. 27101LUN" />
                                </div>
                                <div className="form-group">
                                    <label>Tipo percorso</label>
                                    <select value={cForm.tipoPercorso} onChange={(e) => setCForm((f) => ({ ...f, tipoPercorso: e.target.value }))}>
                                        {TIPI_PERCORSO.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <CampiValidita form={cForm} setForm={setCForm} />
                            </div>

                            <div className="section-label">Tratti del percorso (dall'anagrafica 120)</div>
                            <div className="gp-tratti-grid">
                                {/* Colonna sinistra: ricerca + risultati */}
                                <div className="gp-tratti-col">
                                    <div className="picker-add">
                                        <input
                                            type="text"
                                            placeholder={trattiLoading ? "Caricamento tratti..." : "Cerca un tratto (codice o via)..."}
                                            value={pickerSearch}
                                            onChange={(e) => setPickerSearch(e.target.value)}
                                            disabled={trattiLoading}
                                        />
                                        {pickerSearch && (
                                            <button type="button" className="picker-clear" onClick={() => setPickerSearch("")} title="Pulisci ricerca">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                            </button>
                                        )}
                                    </div>
                                    <div className={`gp-results-box ${pickerSearch ? "has-search" : ""}`}>
                                        {trattiLoading ? (
                                            <div className="gp-box-empty">Caricamento tratti...</div>
                                        ) : !pickerSearch ? (
                                            <div className="gp-box-empty">Scrivi qui sopra per cercare un tratto per codice o via, poi clicca per aggiungerlo.</div>
                                        ) : pickerResults.length === 0 ? (
                                            <div className="gp-box-empty">Nessun tratto trovato.</div>
                                        ) : (
                                            pickerResults.map((t) => (
                                                <div key={t.id} className="picker-result" onClick={() => addTratto(t)}>
                                                    <div className="pr-text">
                                                        <b>{t.strada || "(senza strada)"}</b>
                                                        <span>{t.tratto} · {t.codice}{t.passAnno != null && <span className="gp-pass-badge">{t.passAnno} pass/anno</span>}</span>
                                                    </div>
                                                    <span className="pr-add">+ aggiungi</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                                {/* Colonna destra: tratti selezionati, in ordine */}
                                <div className="gp-tratti-col">
                                    <div className="gp-col-head">Selezionati ({selectedTratti.length})</div>
                                    <div className="gp-selected-box">
                                        {selectedTratti.length === 0 ? (
                                            <div className="gp-box-empty">Nessun tratto selezionato. Aggiungili dalla colonna a sinistra.</div>
                                        ) : (
                                            selectedTratti.map((t, i) => (
                                                <div
                                                    className={`tratto-row ${dragIndex === i ? "gp-dragging" : ""} ${movedId === t.id ? "gp-just-moved" : ""}`}
                                                    key={t.id}
                                                    data-row
                                                    onDragOver={(e) => { if (dragIndex === null) return; e.preventDefault(); if (dragIndex !== i) { moveTratto(dragIndex, i); setDragIndex(i); } }}
                                                    onDrop={(e) => e.preventDefault()}
                                                >
                                                    {/* Desktop (>=1025px): maniglia con drag-and-drop nativo (immagine = riga intera) */}
                                                    <span
                                                        className="gp-drag-handle"
                                                        draggable
                                                        onDragStart={(e) => {
                                                            const row = e.currentTarget.closest("[data-row]");
                                                            const rect = row.getBoundingClientRect();
                                                            e.dataTransfer.effectAllowed = "move";
                                                            try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) { /* Safari */ }
                                                            e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top);
                                                            setDragIndex(i);
                                                        }}
                                                        onDragEnd={() => setDragIndex(null)}
                                                        title="Trascina per riordinare"
                                                        aria-label="Trascina per riordinare questo tratto"
                                                        role="button"
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                                                    </span>
                                                    {/* Mobile/tablet (<=1024px): frecce su/giù */}
                                                    <div className="gp-move-col">
                                                        <button className="gp-move-btn" title="Sposta su" disabled={i === 0} onClick={() => { moveTratto(i, i - 1); flashMoved(t.id); }}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
                                                        </button>
                                                        <button className="gp-move-btn" title="Sposta giù" disabled={i === selectedTratti.length - 1} onClick={() => { moveTratto(i, i + 1); flashMoved(t.id); }}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
                                                        </button>
                                                    </div>
                                                    <div className="idx">{i + 1}</div>
                                                    <div className="t-name"><b>{t.strada || "(senza strada)"}</b><span>{t.tratto} · {t.codice}{t.passAnno != null && <span className="gp-pass-badge">{t.passAnno} pass/anno</span>}</span></div>
                                                    <button className="icon-btn danger" title="Rimuovi" onClick={() => setRemoveViaTarget(t)}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="footer-hint">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                                <span>{selectedTratti.length} tratti selezionati</span>
                            </div>
                            <div className="footer-actions">
                                <button className="btn btn-ghost" onClick={() => setComposerOpen(false)} disabled={saving}>Annulla</button>
                                <button className="btn btn-primary" onClick={savePercorso} disabled={saving}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                    {saving ? "Salvataggio..." : "Salva su Interacta"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== CONFERMA RIMOZIONE VIA DAL PERCORSO ===== */}
            {removeViaTarget && (
                <div className="modal-overlay" style={{ zIndex: 1500 }} {...clicFuori(() => setRemoveViaTarget(null))}>
                    <div className="modal danger" style={{ maxWidth: 440 }}>
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </div>
                            <h3>Rimuovere la via?</h3>
                            <button className="modal-close" onClick={() => setRemoveViaTarget(null)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="modal-text">
                                Vuoi rimuovere <strong>{removeViaTarget.strada || "(senza strada)"}</strong> ({removeViaTarget.codice}) dall'elenco dei tratti del percorso?
                                La modifica sarà salvata su Interacta solo quando premi "Salva su Interacta".
                            </p>
                        </div>
                        <div className="modal-footer">
                            <div />
                            <div className="footer-actions">
                                <button className="btn btn-ghost" onClick={() => setRemoveViaTarget(null)}>Annulla</button>
                                <button className="btn btn-danger" onClick={() => { removeTratto(removeViaTarget.id); setRemoveViaTarget(null); }}>Rimuovi</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== ELIMINA PERCORSO ===== */}
            {deleteTarget && (
                <div className="modal-overlay" {...clicFuori(() => setDeleteTarget(null))}>
                    <div className="modal danger" style={{ maxWidth: 460 }}>
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                            </div>
                            <h3>Confermi l'eliminazione?</h3>
                            <button className="modal-close" onClick={() => setDeleteTarget(null)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="modal-text">
                                Stai per eliminare il percorso <strong>{deleteTarget.nome} ({deleteTarget.customId})</strong> dalla community 121 (Anagrafica Percorsi).
                            </p>
                            {deleteError && <div className="modal-error">{deleteError}</div>}
                        </div>
                        <div className="modal-footer">
                            <div />
                            <div className="footer-actions">
                                <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annulla</button>
                                <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>{deleting ? "Eliminazione..." : "Elimina"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== GENERA PERCORSO OPERATIVO (122 + deep-link) ===== */}
            {genTarget && (
                <div className="modal-overlay" {...clicFuori(() => { if (!generating) setGenTarget(null); })}>
                    <div className="modal wide gp-modal-full gp-modal-fit">
                        <div className="modal-header">
                            <div className="m-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="12" y1="14" x2="12" y2="18" /><line x1="10" y1="16" x2="14" y2="16" /></svg>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Genera percorso operativo</h3>
                                <div className="h-sub">Crea il post nella community 122 con il link all'app operatori</div>
                            </div>
                            <button className="modal-close" onClick={() => setGenTarget(null)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                        {genError && <div className="modal-error">{genError}</div>}
                        <div className="modal-body">
                            <div className="gp-info-section">
                                <h4>{genTarget.nome || "—"}</h4>
                                <p className="muted">Itinerario <strong>{genTarget.itinerario || "—"}</strong> · {genTarget.nTratti} tratti</p>
                            </div>
                            {genResult ? (
                                <div className="gp-info-section">
                                    <h4>Percorso operativo creato ✓</h4>
                                    <p>customId: <strong>{genResult.customId}</strong></p>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: ".5px" }}>Link app operatori</label>
                                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                                        <input type="text" readOnly value={genResult.url || ""} onFocus={(e) => e.target.select()} style={{ flex: 1, padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }} />
                                        <button className="btn btn-ghost" onClick={() => { if (navigator.clipboard && genResult.url) navigator.clipboard.writeText(genResult.url); }}>Copia</button>
                                        <a className="btn btn-ghost" href={genResult.url} target="_blank" rel="noopener noreferrer">Apri</a>
                                    </div>
                                </div>
                            ) : (
                                <>
                                <div className="gp-gen-grid">
                                    <div className="form-group">
                                        <label>Data</label>
                                        <DatePickerModal value={genData} onChange={(e) => setGenData(e.target.value)} label="Data percorso" />
                                        {/* Avviso anticipato: la stessa regola la applica il backend al "Genera". */}
                                        {genData && !attivaIl(genTarget, genData) && (
                                            <div className="muted" style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}>
                                                Questo percorso non è valido per il giro del {genData.split("-").reverse().join("/")}.
                                            </div>
                                        )}
                                        {genData && genTratti.some((t) => t.validita && !attivaIl(t, genData)) && (
                                            <div className="muted" style={{ color: "#DC2626", fontSize: 12, marginTop: 4 }}>
                                                {genTratti.filter((t) => t.validita && !attivaIl(t, genData)).length} tratte non sono valide in questa data: toglile prima di generare.
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Operatori</label>
                                        <Select
                                            isMulti
                                            classNamePrefix="rs"
                                            options={utentiOptions}
                                            value={genOperatori}
                                            onChange={(vals) => setGenOperatori(vals || [])}
                                            placeholder={utentiLoading ? "Caricamento operatori..." : "Seleziona uno o più operatori"}
                                            isLoading={utentiLoading}
                                            noOptionsMessage={() => "Nessun operatore"}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            menuPosition="fixed"
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 3000 }) }}
                                        />
                                    </div>
                                </div>

                                {/* Tratti del post: modifica valida SOLO per questo post 122, l'anagrafica 121 resta invariata. */}
                                <div className="section-label">Tratti del post (modifica solo per questo post)</div>
                                <div className="gp-tratti-grid">
                                    {/* Colonna sinistra: ricerca + risultati */}
                                    <div className="gp-tratti-col">
                                        <div className="picker-add">
                                            <input
                                                type="text"
                                                placeholder={trattiLoading ? "Caricamento tratti..." : "Cerca un tratto (codice o via)..."}
                                                value={genPickerSearch}
                                                onChange={(e) => setGenPickerSearch(e.target.value)}
                                                disabled={trattiLoading}
                                            />
                                            {genPickerSearch && (
                                                <button type="button" className="picker-clear" onClick={() => setGenPickerSearch("")} title="Pulisci ricerca">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                                </button>
                                            )}
                                        </div>
                                        <div className={`gp-results-box ${genPickerSearch ? "has-search" : ""}`}>
                                            {trattiLoading ? (
                                                <div className="gp-box-empty">Caricamento tratti...</div>
                                            ) : !genPickerSearch ? (
                                                <div className="gp-box-empty">Scrivi qui sopra per cercare un tratto per codice o via, poi clicca per aggiungerlo.</div>
                                            ) : genPickerResults.length === 0 ? (
                                                <div className="gp-box-empty">Nessun tratto trovato.</div>
                                            ) : (
                                                genPickerResults.map((t) => (
                                                    <div key={t.id} className="picker-result" onClick={() => addGenTratto(t)}>
                                                        <div className="pr-text">
                                                            <b>{t.strada || "(senza strada)"}</b>
                                                            <span>{t.tratto} · {t.codice}{t.passAnno != null && <span className="gp-pass-badge">{t.passAnno} pass/anno</span>}</span>
                                                        </div>
                                                        <span className="pr-add">+ aggiungi</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                    {/* Colonna destra: tratti selezionati, in ordine */}
                                    <div className="gp-tratti-col">
                                        <div className="gp-col-head">Selezionati ({genTratti.length})</div>
                                        <div className="gp-selected-box">
                                            {genTratti.length === 0 ? (
                                                <div className="gp-box-empty">Nessun tratto selezionato. Aggiungili dalla colonna a sinistra.</div>
                                            ) : (
                                                genTratti.map((t, i) => (
                                                    <div
                                                        className={`tratto-row ${recuperoMap[t.codice]?.livello === "rossa" ? "is-recupero-rossa" : t.prioritaria || recuperoMap[t.codice] ? "is-prioritaria" : ""} ${dragIndex === i ? "gp-dragging" : ""} ${movedId === t.id ? "gp-just-moved" : ""}`}
                                                        key={t.id}
                                                        data-row
                                                        data-tratto-id={t.id}
                                                        onDragOver={(e) => { if (dragIndex === null) return; e.preventDefault(); if (dragIndex !== i) { moveGenTratto(dragIndex, i); setDragIndex(i); } }}
                                                        onDrop={(e) => e.preventDefault()}
                                                    >
                                                        {/* Desktop (>=1025px): maniglia con drag-and-drop nativo. L'immagine
                                                            trascinata è l'intera riga (setDragImage), così si capisce cosa sposti. */}
                                                        <span
                                                            className="gp-drag-handle"
                                                            draggable
                                                            onDragStart={(e) => {
                                                                const row = e.currentTarget.closest("[data-row]");
                                                                const rect = row.getBoundingClientRect();
                                                                e.dataTransfer.effectAllowed = "move";
                                                                try { e.dataTransfer.setData("text/plain", String(i)); } catch (_) { /* Safari */ }
                                                                e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top);
                                                                setDragIndex(i);
                                                            }}
                                                            onDragEnd={() => setDragIndex(null)}
                                                            title="Trascina per riordinare"
                                                            aria-label="Trascina per riordinare questo tratto"
                                                            role="button"
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
                                                        </span>
                                                        {/* Mobile/tablet (<=1024px): frecce su/giù (il drag col dito dava problemi). */}
                                                        <div className="gp-move-col">
                                                            <button className="gp-move-btn" title="Sposta su" disabled={i === 0} onClick={() => { moveGenTratto(i, i - 1); flashMoved(t.id); }}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 15l-6-6-6 6" /></svg>
                                                            </button>
                                                            <button className="gp-move-btn" title="Sposta giù" disabled={i === genTratti.length - 1} onClick={() => { moveGenTratto(i, i + 1); flashMoved(t.id); }}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6" /></svg>
                                                            </button>
                                                        </div>
                                                        {/* Numerino d'ordine editabile: scrivi la posizione voluta e premi Invio (o clicca
                                                            fuori). Le altre tratte scalano di conseguenza. Esc annulla la digitazione. */}
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            className="idx idx-input"
                                                            value={idxEditId === t.id ? idxEditVal : i + 1}
                                                            title="Scrivi la posizione e premi Invio"
                                                            aria-label={`Posizione di ${t.strada || t.codice}: ${i + 1}`}
                                                            onFocus={(e) => { setIdxEditId(t.id); setIdxEditVal(String(i + 1)); e.target.select(); }}
                                                            onChange={(e) => setIdxEditVal(e.target.value.replace(/\D/g, "").slice(0, 3))}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") e.currentTarget.blur();          // il blur applica lo spostamento
                                                                // Esc: rimetto in campo la posizione attuale, così applyIdxEdit esce senza spostare
                                                                if (e.key === "Escape") { setIdxEditVal(String(i + 1)); e.currentTarget.blur(); }
                                                            }}
                                                            onBlur={() => applyIdxEdit(i)}
                                                        />
                                                        <div className="t-name"><b>{t.strada || "(senza strada)"}</b><span>{t.tratto} · {t.codice}{t.passAnno != null && <span className="gp-pass-badge">{t.passAnno} pass/anno</span>}</span></div>
                                                        {/* Una sola stella per riga: il ROSSO automatico prevale sul giallo.
                                                            Il clic agisce sempre e solo sulla priorità MANUALE; quella
                                                            automatica resta comunque visibile finché la tratta non è fatta. */}
                                                        {(() => {
                                                            const auto = recuperoMap[t.codice];
                                                            const accesa = !!auto || !!t.prioritaria;
                                                            const rossa = auto?.livello === "rossa";
                                                            const volte = auto?.consecutive || 0;
                                                            const titoloAuto = `Non effettuata ${volte} volt${volte === 1 ? "a" : "e"} di fila — da recuperare`;
                                                            return (
                                                                <button
                                                                    className={`icon-btn gp-star-btn ${accesa ? "on" : ""} ${rossa ? "gp-star-rossa" : ""}`}
                                                                    title={[
                                                                        auto ? titoloAuto : null,
                                                                        t.prioritaria ? "Prioritaria per questo percorso — clic per togliere" : "Segna prioritaria per questo percorso",
                                                                    ].filter(Boolean).join(" · ")}
                                                                    onClick={() => toggleGenPriorita(t.id)}
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill={accesa ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                                                                </button>
                                                            );
                                                        })()}
                                                        <button className="icon-btn danger" title="Rimuovi" onClick={() => removeGenTratto(t.id)}>
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <div />
                            <div className="footer-actions">
                                {genResult ? (
                                    <button className="btn btn-primary" onClick={() => setGenTarget(null)}>Chiudi</button>
                                ) : (
                                    <>
                                        <button className="btn btn-ghost" onClick={() => setGenTarget(null)} disabled={generating}>Annulla</button>
                                        <button className="btn btn-primary" onClick={submitGenera} disabled={generating}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                            {generating ? "Generazione..." : "Genera"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TabAnagraficaPercorsi;
